import React, { useState, useEffect } from 'react';
import './App.css';
import ChannelCard from './components/ChannelCard';
import AddChannelCard from './components/AddChannelCard';
import { Channel, ChannelFormData } from './types/Channel';
import { saveChannels, loadChannels, generateChannelId, resetToDefaults } from './utils/channelStorage';

function App() {
  const [channels, setChannels] = useState<Channel[]>([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [saveStatus, setSaveStatus] = useState<'saved' | 'saving' | null>(null);
  const [touchStartIndex, setTouchStartIndex] = useState<number | null>(null);
  const [touchStartPos, setTouchStartPos] = useState<{ x: number; y: number } | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    const loadedChannels = loadChannels();
    setChannels(loadedChannels);
  }, []);

  const saveChannelsWithStatus = (channelsToSave: Channel[]) => {
    setSaveStatus('saving');
    saveChannels(channelsToSave);
    setSaveStatus('saved');
    setTimeout(() => setSaveStatus(null), 2000);
  };

  const handleUpdateChannel = (channelId: string, updatedData: ChannelFormData) => {
    const updatedChannels = channels.map(channel =>
      channel.id === channelId
        ? { ...channel, ...updatedData }
        : channel
    );
    setChannels(updatedChannels);
    saveChannelsWithStatus(updatedChannels);
  };

  const handleDeleteChannel = (channelId: string) => {
    const updatedChannels = channels.filter(channel => channel.id !== channelId);
    setChannels(updatedChannels);
    saveChannelsWithStatus(updatedChannels);
  };

  const handleAddChannel = (channelData: ChannelFormData) => {
    const newChannel: Channel = {
      id: generateChannelId(),
      ...channelData
    };
    const updatedChannels = [...channels, newChannel];
    setChannels(updatedChannels);
    saveChannelsWithStatus(updatedChannels);
  };

  const toggleEditMode = () => {
    if (isEditMode) {
      // When exiting edit mode, ensure all settings are saved
      setSaveStatus('saving');
      saveChannels(channels);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus(null), 2000);
    }
    setIsEditMode(!isEditMode);
  };

  const handleResetChannels = () => {
    if (window.confirm('Are you sure you want to reset all channels to defaults? This will remove any custom channels you\'ve added.')) {
      const defaultChannels = resetToDefaults();
      setChannels(defaultChannels);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus(null), 2000);
    }
  };

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      return;
    }

    const newChannels = [...channels];
    const draggedChannel = newChannels[draggedIndex];
    
    // Remove the dragged item
    newChannels.splice(draggedIndex, 1);
    
    // Insert it at the new position
    newChannels.splice(dropIndex, 0, draggedChannel);
    
    setChannels(newChannels);
    saveChannelsWithStatus(newChannels);
    setDraggedIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  // Touch event handlers for mobile drag and drop
  const handleTouchStart = (e: React.TouchEvent, index: number) => {
    if (!isEditMode) return;
    
    const touch = e.touches[0];
    setTouchStartIndex(index);
    setTouchStartPos({ x: touch.clientX, y: touch.clientY });
    setDraggedIndex(index);
    
    // Add initial visual feedback
    const target = e.currentTarget as HTMLElement;
    target.style.transition = 'transform 0.2s ease';
    target.style.transform = 'scale(1.05)';
    target.style.zIndex = '1000';
    target.style.opacity = '0.9';
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (touchStartIndex === null || !touchStartPos) return;
    
    e.preventDefault(); // Prevent scrolling
    const touch = e.touches[0];
    const deltaX = touch.clientX - touchStartPos.x;
    const deltaY = touch.clientY - touchStartPos.y;
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    
    // Start dragging visual effect after 15px movement
    if (distance > 15 && !isDragging) {
      setIsDragging(true);
      const target = e.currentTarget as HTMLElement;
      target.style.transition = 'none'; // Remove transition for smooth following
    }
    
    if (isDragging) {
      // Make the element follow the finger
      const target = e.currentTarget as HTMLElement;
      target.style.transform = `translate(${deltaX}px, ${deltaY}px) scale(1.1) rotate(5deg)`;
      target.style.boxShadow = '0 20px 40px rgba(0, 0, 0, 0.4)';
      
      // Find the element under the touch point (excluding the dragged element)
      target.style.pointerEvents = 'none'; // Temporarily disable pointer events
      const elementBelow = document.elementFromPoint(touch.clientX, touch.clientY);
      target.style.pointerEvents = 'auto'; // Re-enable pointer events
      
      const channelWrapper = elementBelow?.closest('.channel-wrapper');
      
      // Remove previous drag-over classes
      document.querySelectorAll('.channel-wrapper').forEach(el => {
        el.classList.remove('drag-over');
      });
      
      if (channelWrapper && channelWrapper !== target) {
        channelWrapper.classList.add('drag-over');
      }
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartIndex === null || !touchStartPos) {
      setDraggedIndex(null);
      setIsDragging(false);
      return;
    }
    
    const touch = e.changedTouches[0];
    const target = e.currentTarget as HTMLElement;
    
    // Temporarily disable pointer events to get element below
    target.style.pointerEvents = 'none';
    const elementBelow = document.elementFromPoint(touch.clientX, touch.clientY);
    target.style.pointerEvents = 'auto';
    
    const channelWrapper = elementBelow?.closest('.channel-wrapper');
    
    // Animate back to position
    target.style.transition = 'all 0.3s ease';
    target.style.transform = '';
    target.style.zIndex = '';
    target.style.opacity = '';
    target.style.boxShadow = '';
    
    // Remove all drag-over classes
    document.querySelectorAll('.channel-wrapper').forEach(el => {
      el.classList.remove('drag-over');
    });
    
    if (channelWrapper) {
      const allWrappers = document.querySelectorAll('.channel-wrapper');
      const dropIndex = Array.from(allWrappers).indexOf(channelWrapper as Element);
      
      if (dropIndex !== -1 && dropIndex !== touchStartIndex) {
        // Perform the reorder
        const newChannels = [...channels];
        const draggedChannel = newChannels[touchStartIndex];
        
        // Remove the dragged item
        newChannels.splice(touchStartIndex, 1);
        
        // Insert it at the new position
        newChannels.splice(dropIndex, 0, draggedChannel);
        
        setChannels(newChannels);
        saveChannelsWithStatus(newChannels);
        
        // Provide haptic feedback if available
        if ('vibrate' in navigator) {
          navigator.vibrate(50);
        }
        
        // Add success animation
        setTimeout(() => {
          channelWrapper.classList.add('drop-success');
          setTimeout(() => {
            channelWrapper.classList.remove('drop-success');
          }, 600);
        }, 100);
      }
    }
    
    // Reset touch state
    setTouchStartIndex(null);
    setTouchStartPos(null);
    setDraggedIndex(null);
    setIsDragging(false);
  };

  return (
    <div className="App">
      <header className="app-header">
        <div className="header-left">
          <h1>La Stream</h1>
          {isEditMode && <span className="edit-mode-indicator">Drag to reorder channels</span>}
          {saveStatus && (
            <span className={`save-status ${saveStatus}`}>
              {saveStatus === 'saving' ? 'Saving...' : 'All changes saved'}
            </span>
          )}
        </div>
        <div className="header-buttons">
          <button 
            className={`edit-toggle ${isEditMode ? 'active' : ''}`}
            onClick={toggleEditMode}
          >
            {isEditMode ? 'Exit Edit' : 'Edit'}
          </button>
          {isEditMode && (
            <button 
              className="reset-button"
              onClick={handleResetChannels}
              title="Reset to default channels"
            >
              Reset
            </button>
          )}
        </div>
      </header>
      
      <main className="app-main">
        <div className="channels-grid">
          {channels.map((channel, index) => (
            <div
              key={channel.id}
              draggable={isEditMode}
              onDragStart={(e) => handleDragStart(e, index)}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, index)}
              onDragEnd={handleDragEnd}
              onTouchStart={(e) => handleTouchStart(e, index)}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              className={`channel-wrapper ${isEditMode ? 'draggable' : ''} ${
                draggedIndex === index ? 'dragging' : ''
              } ${touchStartIndex === index && isDragging ? 'touch-dragging' : ''}`}
            >
              <ChannelCard
                channel={channel}
                isEditMode={isEditMode}
                onUpdate={handleUpdateChannel}
                onDelete={handleDeleteChannel}
              />
            </div>
          ))}
          <AddChannelCard
            isEditMode={isEditMode}
            onAdd={handleAddChannel}
          />
        </div>
      </main>
    </div>
  );
}

export default App;
